import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLogger, useTheme } from "cayo.ui";
import { Entity } from "@cayo/shared/api/types/cayo-graph";
import useFormMessageBar from "@cayo/shared/components/Form/hooks/useFormMessageBar";
import { useTabNavigation } from "@cayo/shared/components/Form/hooks/useTabNavigation";
import {
  FormConfigProps,
  FormProps,
  IAdditionalFormProps,
} from "@cayo/shared/components/Form/types";
import { commonMessages } from "@cayo/shared/messages/common";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import downloadUtils from "@cayo/shared/utils/download";
import useFormModel from "./useFormModel";
import odataUtils from "../../../utils/odata-utils";
import scheduleTypes from "../../Schedule/types";
import modelUtils from "../modelUtils";
import renderForm from "../renderers";

const useForm = (props: FormProps, object?: Entity, config?: FormConfigProps) => {
  const intl = useIntl();
  const log = useLogger("form.hook");
  const theme = useTheme();

  const [hasFooter, setHasFooter] = useState(props?.alwaysShowFooter);

  const {
    actionBuilder,
    propertyAnnotations,
    onSubmit: onParentSubmit,
    bodyProperty,
    isAdminProps,
    ...formProps
  } = props;

  const { buildSubmit, processResponse } = actionBuilder;
  const {
    extendedProps,
    model,
    setModel,
    isSaveDisabled,
    variables,
    errors,
    setErrorCallback,
    countErrors,
    clearErrors,
    isLoading: isFormInitializing,
  } = useFormModel(props, object);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { message, setMessage } = useFormMessageBar(props?.submit?.url || ""); // TODO: use something else as formId

  const onValidate = useCallback(
    (currentIndex?: number) => {
      setMessage(undefined);

      const totalErrors = countErrors(model, currentIndex);

      if (totalErrors) {
        const msg = `Total ${totalErrors} errors found`;
        log.debug(msg);
        setMessage({ type: "error", message: msg });

        return Promise.reject(msg);
      }

      return Promise.resolve();
    },
    [model]
  );

  const { setTabNavigation, tabNavigation } = useTabNavigation(onValidate);

  const onSubmit = useCallback(() => {
    log.debug("Begin form submittion");
    setSubmitted(true);

    const totalErrors = countErrors(model);

    if (totalErrors) {
      const msg = `Total ${totalErrors} errors found`;
      log.debug(msg);
      setMessage({ type: "error", message: msg });

      return Promise.reject();
    }

    let sendingModel = modelUtils.getModel(extendedProps, model);

    if (object && !isAdminProps) {
      const model = modelUtils.normalizeModelForComplexType(object, sendingModel);
      const oDatatypes = modelUtils.getODataTypes(object, sendingModel);

      sendingModel = {
        ...model,
        ...oDatatypes,
      };
    }

    const odataType = (props as any)["@odata.type"];
    if (odataType) {
      sendingModel["@odata.type"] = odataType;
    }

    if (bodyProperty) {
      const isDummyType =
        sendingModel["@odata.type"] === "#" + ("cayo.graph.notSetSchedule" as scheduleTypes);

      sendingModel = { [bodyProperty]: isDummyType ? null : sendingModel };
    }

    if (/*!extendedProps.submit && */ onParentSubmit) {
      return onParentSubmit(sendingModel, object, true)
        .then(() => {
          extendedProps.signals?.send("close");
        })
        .catch((e) => {
          ajaxUtils
            .getError(e)
            .then((err) => setMessage({ type: "error", message: err?.error_description }));
        });
    }

    setIsSubmitting(true);
    log.debug("Model is ok", sendingModel);

    return buildSubmit(extendedProps.submit!, sendingModel)
      ?.then((response) => {
        log.debug("Form has submitted, response", response);

        setIsSubmitting(false);

        if (formProps.submit?.isStream) {
          const headers = ajaxUtils.getHeaders(response);
          downloadUtils.downloadStream(response, headers);
        }

        if (formProps.submit?.response) {
          processResponse(
            response,
            model,
            formProps.submit?.response,
            variables,
            extendedProps.signals
          );
        } else if (!props.isInsidePropPanel) {
          props.onClose && props.onClose(response);
        }

        setMessage({
          type: "success",
          message: intl.formatMessage(commonMessages.propertiesUpdated, {
            objectName: object?.objectName ?? props?.parentObjectName,
          }),
        });

        if (props?.onSubmitCB) {
          props?.onSubmitCB(response);
        }
      })
      .catch((e) => {
        return ajaxUtils.getError(e).then((ee) => {
          log.error("Failed to submit form", ee);

          const errorMessage = odataUtils.errorToString(ee);

          setMessage({ type: "error", message: errorMessage });

          return Promise.reject(errorMessage);
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [model, object, onParentSubmit]);

  const addProps = useMemo(() => {
    return {
      model,
      setModel,
      errors,
      setErrorCallback,
      intl,
      isSaveDisabled,
      object,
      propertyAnnotations,
      setMessage,
      theme,
      getIconAnnotation: props?.getIconAnnotation,
      signals: props?.signals as any,
      parentModel: props?.parentModel,
      handleResponse: props?.handleResponse,
      isWizard: props?.isWizard,
      tabNavigation: props?.isWizard ? tabNavigation : undefined,
      setTabNavigation: props?.isWizard ? setTabNavigation : undefined,
      formId: config?.formId,
    } as IAdditionalFormProps;
  }, [
    model,
    setModel,
    errors,
    setErrorCallback,
    isSaveDisabled,
    object,
    propertyAnnotations,
    setMessage,
    props?.getIconAnnotation,
    props?.signals,
    props?.parentModel,
    config?.formId,
  ]);

  useEffect(() => {
    if (extendedProps.footer && !extendedProps.alwaysShowFooter) {
      if (!extendedProps?.items?.length || props?.alwaysEnableSubmit) {
        return setHasFooter(true);
      }

      const hasEditableField = extendedProps?.items?.some((i) => !i.readOnly && !i.isStandalone);
      setHasFooter(!!hasEditableField);
    }
  }, [extendedProps]);

  useEffect(() => {
    if (config?.validateOnTyping && submitted) {
      countErrors(model);
    }
  }, [model, submitted, config]);

  const renderer = useCallback(() => {
    return renderForm(extendedProps, addProps);
  }, [addProps, extendedProps]);

  return {
    isSubmitting,
    isSaveDisabled,
    onSubmit,
    errors,
    clearErrors,
    message: !props?.isInsidePropPanel && message?.type === "success" ? undefined : message,
    renderer,
    isInputValid: !errors || !Object.keys(errors).length,
    model,
    isFormInitializing,
    hasFooter,
    tabNavigation,
  };
};

export default useForm;
